// Character creation — anchor-selector style. Each pick gates the next.
// Visible only when phase = "need_character".

import type { Net } from "../net.js";
import type { ClientState } from "../state.js";
import type { Faction } from "../../shared/protocol.js";

const CLASSES_BY_FACTION: Record<Faction, string[]> = {
  hammer:     ["Knight", "Inquisitor", "Templar", "Cleric"],
  maleficium: ["Witch", "Sorcerer", "Warlock", "Hexenmeister"],
  neutral:    ["Wanderer", "Hunter", "Scholar"],
};

const ORIGINS_BY_FACTION: Record<Faction, string[]> = {
  hammer:     ["Schlettenheim", "Wiesenthal"],
  maleficium: ["Wiesenthal", "Schwarzwald"],
  neutral:    ["Wiesenthal", "Oberweiler"],
};

const FACTION_BLURB: Record<Faction, string> = {
  hammer: "Holy warriors and Dominican inquisitors. Your pact is with God; your weapon, His word.",
  maleficium: "Witches and sorcerers. Your pact is with darker things, and the price is heavier than you know.",
  neutral: "Unaligned. The world will pull you toward one side or the other; resist it as long as you can.",
};

interface Draft {
  faction?: Faction;
  class?: string;
  origin?: string;
  name?: string;
  gender?: "male" | "female";
}

export function renderCharacterCreate(host: HTMLElement, net: Net, state: ClientState): void {
  // If they already have characters, offer to play one OR create a new one.
  const existing = state.characters;
  const draft: Draft = {};

  function rerender(): void {
    let html = `<div class="panel-card">
      <h2>Choose your character</h2>`;

    if (existing.length > 0) {
      html += `<p class="dim">Existing characters:</p>
        <div class="char-list">${existing.map((c) => `
          <button class="char-row" data-id="${c.id}">
            <span class="big">${escape(c.name)}</span>
            <span class="dim">${escape(c.faction)} ${escape(c.class)}, lvl ${c.level} — ${escape(c.origin)}</span>
          </button>`).join("")}
        </div>
        <hr />`;
    }

    html += `<p class="dim">…or forge a new one. Each choice locks the next.</p>`;

    // Step 1: Faction
    html += `<div class="anchor"><span class="lbl">Faction</span>
      ${(["hammer", "maleficium", "neutral"] as Faction[]).map((f) => `
        <button class="opt ${draft.faction === f ? "sel" : ""}" data-step="faction" data-val="${f}">${cap(f)}</button>
      `).join("")}
    </div>`;
    if (draft.faction) {
      html += `<p class="dim">${FACTION_BLURB[draft.faction]}</p>`;
    }

    // Step 2: Class (gated)
    if (draft.faction) {
      html += `<div class="anchor"><span class="lbl">Class</span>
        ${CLASSES_BY_FACTION[draft.faction].map((c) => `
          <button class="opt ${draft.class === c ? "sel" : ""}" data-step="class" data-val="${escape(c)}">${escape(c)}</button>
        `).join("")}
      </div>`;
    }

    // Step 3: Origin (gated by faction)
    if (draft.faction && draft.class) {
      html += `<div class="anchor"><span class="lbl">Origin</span>
        ${ORIGINS_BY_FACTION[draft.faction].map((o) => `
          <button class="opt ${draft.origin === o ? "sel" : ""}" data-step="origin" data-val="${escape(o)}">${escape(o)}</button>
        `).join("")}
      </div>`;
    }

    // Step 4: Gender
    if (draft.faction && draft.class && draft.origin) {
      html += `<div class="anchor"><span class="lbl">Gender</span>
        ${(["female", "male"] as const).map((g) => `
          <button class="opt ${draft.gender === g ? "sel" : ""}" data-step="gender" data-val="${g}">${cap(g)}</button>
        `).join("")}
      </div>`;
    }

    // Step 5: Name + confirm
    if (draft.faction && draft.class && draft.origin && draft.gender) {
      html += `<div class="anchor"><span class="lbl">Name</span>
        <input id="char-name" type="text" maxlength="32" value="${escape(draft.name ?? "")}" placeholder="e.g. Sigrid the Pious" />
        <button id="forge" class="primary" ${draft.name && draft.name.length >= 2 ? "" : "disabled"}>Forge character</button>
      </div>`;
    }

    html += `</div>`;
    host.innerHTML = html;

    // Wire option buttons
    for (const b of host.querySelectorAll<HTMLButtonElement>("button.opt")) {
      b.addEventListener("click", () => {
        const step = b.dataset.step!;
        const val  = b.dataset.val!;
        if (step === "faction") {
          draft.faction = val as Faction;
          // Cascade-clear downstream
          delete draft.class; delete draft.origin; delete draft.gender; delete draft.name;
        } else if (step === "class")  draft.class = val;
        else if (step === "origin")   draft.origin = val;
        else if (step === "gender")   draft.gender = val as "male" | "female";
        rerender();
      });
    }
    // Wire existing-character buttons
    for (const b of host.querySelectorAll<HTMLButtonElement>("button.char-row")) {
      b.addEventListener("click", () => {
        const id = Number(b.dataset.id);
        net.send({ t: "select_character", id });
      });
    }
    // Wire name input
    const nameInput = host.querySelector<HTMLInputElement>("#char-name");
    if (nameInput) {
      nameInput.addEventListener("input", () => {
        draft.name = nameInput.value.trim();
        const forge = host.querySelector<HTMLButtonElement>("#forge");
        if (forge) forge.disabled = !(draft.name && draft.name.length >= 2);
      });
      nameInput.focus();
    }
    // Wire forge button
    const forge = host.querySelector<HTMLButtonElement>("#forge");
    if (forge) {
      forge.addEventListener("click", () => {
        if (!draft.faction || !draft.class || !draft.origin || !draft.gender || !draft.name) return;
        net.send({
          t: "create_character",
          name: draft.name, faction: draft.faction,
          class: draft.class, origin: draft.origin, gender: draft.gender,
        });
      });
    }
  }

  rerender();
}

function escape(s: string): string {
  return s.replace(/[&<>"]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", "\"": "&quot;" }[c]!));
}
function cap(s: string): string { return s.charAt(0).toUpperCase() + s.slice(1); }
